import React, { useContext, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { CameraControls, PerspectiveCamera, OrthographicCamera } from '@react-three/drei';

import { sceneUnitsFromMm } from '../utils/scaler';

import { emitter } from '../input/AppEvents';

import { ViewerModeEnum } from '../store/viewerState/ViewerMode/ViewerMode';
import { setControlsByViewerMode } from '../store/viewerState/ViewerMode/ViewerMode';

import LayerMappingsEnum from '../enums/LayerMappingsEnum';
import ViewerCameraTypeEnum from '../enums/ViewerCameraTypeEnum';

//import CameraPropertiesUi from './debug-components/CameraPropertiesUI';

import { useModelPropertiesStore } from '../store/modelPropertiesStore/modelPropertiesStore';
import { useViewerStateStore, ViewerLoadingStateEnum } from '../store/viewerStateStore';
import { useCameraControlsStore } from '../store/cameraControlsStore';
import { useServicesState } from '../store/servicesStateStore';
import CameraTargetVisualizer from './debug-components/CameraTargetVisualizer';
import KeyboardControlsComponent from './functional-components/KeyboardControlsComponent';

// Amount of extra space around the model in xy-plane in the camera target boundary
const CAMERA_TARGET_BOUNDARY_EXPANSION_AMOUNT = sceneUnitsFromMm(2000);

const SceneDirector = () => {
    
    const { camera, raycaster } = useThree();
    const { isDebugMode,viewerMode, setViewerMode, loadingState } = useViewerStateStore();
    const { setControls } = useCameraControlsStore();
    
    const controlsRef = useRef<CameraControls | null>(null);
    const perspectiveCameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const orthographicCameraRef = useRef<THREE.OrthographicCamera | null>(null);
    const previousCameraRef = useRef<THREE.Camera | null>(null);
    
    const [usePerspective, setUsePerspective] = useState<boolean>(true);

    const { boundingBox, rotationOffset } = useModelPropertiesStore();
    const { canvasInputListener } = useServicesState();

    useEffect( () => {
        if (loadingState === ViewerLoadingStateEnum.ProductsRendered) {
            setViewerMode(ViewerModeEnum.ApartmentSelect2d);

            //const {boundingBox: finalBoundingBox, angle: finalAngle} = getModelBoundingBoxAndDirection(camera.parent!, [], true);

            /* if (!finalBoundingBox.isEmpty())
            {
                // Set bounding box and rotation offset
                // This will trigger the useEffect which focuses the camera
                // on the model and applies rotation offset
                setBoundingBox(finalBoundingBox);
                setRotationOffset(finalAngle);
            } */
        }

    }, [loadingState]);

    // On ViewerMode or camera change
    useEffect(() => {

         // Set same layers to camera and raycaster
         camera.layers.disableAll();
         raycaster.layers.disableAll();
 
         if (viewerMode) {

            // Set layers of the current viewer mode
            camera.layers.enable(LayerMappingsEnum.Default);
            raycaster.layers.enable(LayerMappingsEnum.Default);
    
            viewerMode?.layers.forEach((layer) => {
                
                camera.layers.enable(layer);
                raycaster.layers.enable(layer);
            });

            // Set controls based on viewer mode
            if (controlsRef.current)
            {
               setControlsByViewerMode(controlsRef.current, viewerMode.modeEnum, canvasInputListener);
            }
         }
 
         // Change input events based on viewer mode
         /* viewerStateDispatch( { type: ViewerStateActionEnum.SET_PRODUCT_INPUT_EVENTS,
                                data: getProductInputEventsByViewerMode(  viewerState.viewerMode?.modeEnum 
                                                               ?? ViewerModeEnum.NotDefined, viewerStateDispatch) });
 */
        setUsePerspective(viewerMode?.cameraType === ViewerCameraTypeEnum.Perspective);

        
            // Bring model into view and zoom to fit view
        if (boundingBox && controlsRef.current)
        {
            if (!boundingBox.isEmpty())
            {
                const focusPoint = boundingBox.getCenter(new THREE.Vector3());    
                controlsRef.current.setLookAt(  focusPoint.x, focusPoint.y, focusPoint.z + sceneUnitsFromMm(10000),
                                        focusPoint.x, focusPoint.y, focusPoint.z , false);
    
                controlsRef.current.fitToBox(boundingBox, false, { paddingLeft: 5, paddingRight: 5, paddingBottom: 10, paddingTop: 10 })

                // Set camera target boundary
                const cameraTargetBoundary = new THREE.Box3(    new THREE.Vector3(  boundingBox.min.x - CAMERA_TARGET_BOUNDARY_EXPANSION_AMOUNT, 
                                                                                    boundingBox.min.y - CAMERA_TARGET_BOUNDARY_EXPANSION_AMOUNT, 
                                                                                    boundingBox.min.z), 
                                                                new THREE.Vector3(  boundingBox.max.x + CAMERA_TARGET_BOUNDARY_EXPANSION_AMOUNT, 
                                                                                    boundingBox.max.y + CAMERA_TARGET_BOUNDARY_EXPANSION_AMOUNT, 
                                                                                    boundingBox.max.z - sceneUnitsFromMm(500)));
                
                controlsRef.current.setBoundary(cameraTargetBoundary);
                controlsRef.current.boundaryFriction = 0.0;          // no bounce/slide when hitting edge
                controlsRef.current.boundaryEnclosesCamera = false;  // only constrain target, let camera move freely in Z

                /* controlsRef.current.setBoundary(boundingBox.clone().expandByScalar(10)); */
            }
            else
                console.warn("Failed to focus on model; Model bounding box was empty");
        }
    
        // Bring model into view and zoom to fit view
        if (rotationOffset && controlsRef.current)
        {
            controlsRef.current.rotate(rotationOffset, 0, false);
        }


    

        
        
    }, [ viewerMode, camera, boundingBox, controlsRef ]);

    // On usePerspective changed
    useEffect(() => {
        if (controlsRef.current)
        {
            const position = controlsRef.current.getPosition(new THREE.Vector3(), true);
            controlsRef.current.camera = camera;
            controlsRef.current?.setLookAt( position.x, position.y, position.z,
                                            position.x, position.y, position.z-1.0, true);
        }
    }, [ usePerspective]);

    // On Toggle debug mode on/off
    useEffect(() => {

        if (isDebugMode) {
          camera.layers.enable(LayerMappingsEnum.DebugEntities);
        } else {
          camera.layers.disable(LayerMappingsEnum.DebugEntities);
        }
      }, [isDebugMode]);

      const enableCanvasInputListener = () => {
        canvasInputListener.enable();
        // Input is live again before view-angle-dependent UI rebuilds itself.
        emitter.emit('cameraRest');
    };
      const disableCanvasInputListener = () => { 
        canvasInputListener.disable(); 
    };

      useEffect(() => {
        // Capture the ref value at setup time so the cleanup closure removes
        // listeners from the exact same object they were added to.
        const controls = controlsRef.current;
        if (controls) {

            setControls(controls);
            controls.restThreshold = 0.01; // Set the threshold for the rest state

            // Add the event listener
            controls.addEventListener('transitionstart', disableCanvasInputListener);
            //controls.addEventListener('controlstart', disableCanvasInputListener);
            //controls.addEventListener('control', disableCanvasInputListener);

            controls.addEventListener('rest', enableCanvasInputListener);
            //controls.addEventListener('controlend', enableCanvasInputListener);
        }

        // Cleanup on unmount
        return () => {
            controls?.removeEventListener('transitionstart', disableCanvasInputListener);
            //controls?.removeEventListener('controlstart', disableCanvasInputListener);
            //controls?.removeEventListener('control', disableCanvasInputListener);

            controls?.removeEventListener('rest', enableCanvasInputListener);
            //controls?.removeEventListener('controlend', enableCanvasInputListener);
        };

    }, [controlsRef.current, camera]);

    // On first render
    useEffect(() => {
        // Subscribe to the event
        const handleViewerModeChange = (mode: ViewerModeEnum) => {
            setViewerMode(mode);
        };
            
        emitter.on('viewerModeChange', handleViewerModeChange);
        
        // Cleanup on unmount
        return () => {
          emitter.off('viewerModeChange', handleViewerModeChange);
        };

      }, []);
      
    return (
        <>
        {   usePerspective ? 
            <PerspectiveCamera name="3D-camera" ref={perspectiveCameraRef}
                makeDefault // Ensures this camera is the active one
                position={[0, 0, 5]}
                fov={75}
                up={[0, 0, 1]} // Set camera up vector,
                near={sceneUnitsFromMm(100)}   // Set near clipping distance
                far={sceneUnitsFromMm(100000)} // Set far clipping distance
            />
            :
            <OrthographicCamera name="2D-camera" ref={orthographicCameraRef}
                makeDefault
                position={[0, 0, 10]} // Set initial camera position
                up={[0, 0, 1]} // Set camera up vector
                zoom={25} // Adjust zoom level
                near={sceneUnitsFromMm(0.1)}   // Set near clipping distance
                far={sceneUnitsFromMm(1000000)} // Set far clipping distance
                
            />
        }

        <CameraControls ref={controlsRef} 
                        minZoom={20}
                        maxZoom={3000}
                        minDistance={sceneUnitsFromMm(1000)} 
                        maxDistance={sceneUnitsFromMm(30000)}
                        dollyToCursor={true} 
                        infinityDolly={true} // Past minDistance, dolly pushes the target forward instead of stalling in front of it
                        enabled={true}
                        
                         />

        { isDebugMode && controlsRef.current && <CameraTargetVisualizer controlsRef={controlsRef} /> }

        { controlsRef.current && viewerMode?.useKeyboardControls && <KeyboardControlsComponent cameraControls={controlsRef.current} speedFactor={sceneUnitsFromMm(2)} /> }
        
       {/* <CameraPropertiesUi controls={controlsRef.current} /> */}
       
        </>
    );
};

export default SceneDirector;